/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useAiProfilesColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for AI Profiles column definitions with custom renderers.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { Bot } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import type { AiProfile } from '../api/aiProfilesApi';

export function useAiProfilesColumns() {
  const { t, refreshKey, i18n } = useI18n();

  const availableColumns = useMemo(
    () => [
      { key: 'profile_name', label: t('aiProfiles.fields.profileName.label'), visible: true, sortable: true },
      { key: 'provider', label: t('aiProfiles.fields.provider.label'), visible: true, sortable: true },
      { key: 'model', label: t('aiProfiles.fields.model.label'), visible: true, sortable: true },
      { key: 'status', label: t('aiProfiles.fields.status.label'), visible: true, sortable: true },
      { key: 'last_tested', label: t('aiProfiles.fields.lastTested.label'), visible: false, sortable: true },
      { key: 'created_at', label: t('common.created'), visible: false, sortable: true },
      { key: 'actions', label: t('common.actions_title'), visible: true, sortable: false }
    ],
    [t, refreshKey, i18n.language]
  );

  const getColumnRenderer = (columnKey: string) => {
    switch (columnKey) {
      case 'provider':
        return (_value: unknown, item: AiProfile) => (
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{item.provider_name || item.provider_type || 'N/A'}</span>
          </div>
        );
      case 'model':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (value: any) => <span className="text-sm text-muted-foreground">{value || 'N/A'}</span>;
      case 'status':
        return (value: boolean) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${
              value
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}
          >
            {value ? t('common.status.active') : t('common.status.inactive')}
          </span>
        );
      case 'last_tested':
        return (value: string | undefined) => (
          <span className="text-sm text-muted-foreground">
            {value ? new Date(value).toLocaleString() : t('common.never')}
          </span>
        );
      case 'created_at':
        return (value: string) => (
          <span className="text-sm text-muted-foreground">{new Date(value).toLocaleDateString()}</span>
        );
      default:
        return undefined;
    }
  };

  return {
    availableColumns,
    getColumnRenderer
  };
}

